import type { SheetPixels } from "common/types/nominalNumbersTypes";
import type { TextVerticalAlignment } from "common/utils/textAlignment";
import { TextAlignment } from "common/utils/textAlignment";
import { cubicCurveTo, quadraticCurveTo } from "geom/bezier";
import type { Mat3 } from "geom/mat3";
import { mat3Ident, mat3Mult, mat3Scale } from "geom/mat3";
import { type Vec2 } from "geom/vec2";
import type { SVGParserCallbacks, SVGPathStyle } from "svg/svgParser";

const combinedTransform: Mat3 = mat3Ident();

/* Unlike THREE.Path, this Path maintains only one curve, eagerly transforming everything to an output type as
 * soon as possible. Therefore it is sufficient to only represent the last curve for continued lineTo operations.
 */
export class DirectPath {
  public autoClose: boolean = false;
  protected lastCurve: [number, number][] = [];
  private viewBox?: { x: number; y: number; width: number; height: number };
  private svgWidth?: number;
  private svgHeight?: number;

  constructor(protected readonly transform: Mat3) {}

  private tryAdjustScale() {
    if (this.viewBox && this.svgWidth && this.svgHeight) {
      const { width, height } = this.viewBox;
      if (width === 0 || height === 0) {
        return;
      }

      const sx = this.svgWidth / width;
      const sy = this.svgHeight / height;

      const scale = mat3Scale(sx, sy);
      mat3Mult(this.transform, scale, this.transform);
    }
  }

  setViewBox(x: number, y: number, width: number, height: number) {
    this.viewBox = { x, y, width, height };
    this.tryAdjustScale();
  }

  setSvgWidth(width: number) {
    this.svgWidth = width;
    this.tryAdjustScale();
  }

  setSvgHeight(height: number) {
    this.svgHeight = height;
    this.tryAdjustScale();
  }

  reset() {
    while (this.lastCurve.length) {
      this.lastCurve.pop();
    }
    this.autoClose = false;
  }

  resetWithPoint(x: number, y: number) {
    this.reset();
    this.appendPoint(x, y);
  }

  appendPoint(x: number, y: number) {
    this.lastCurve.push([x, y]);
  }

  renderCurve(svgTransform: Mat3, style?: SVGPathStyle) {
    if (this.lastCurve.length > 0) {
      if (this.autoClose) {
        this.lastCurve.push([...this.lastCurve[0]]);
        this.autoClose = false;
      }
      mat3Mult(this.transform, svgTransform, combinedTransform);
      this.drawPolyline(this.lastCurve, combinedTransform, style);
    }
  }

  protected drawPolyline(_points: [number, number][], _transform: Mat3, _style?: SVGPathStyle) {
    // Expected to be overridden by subclasses.
  }

  text(
    x: number,
    y: number,
    svgTransform: Mat3,
    fontSize: SheetPixels,
    text: string,
    style: SVGPathStyle,
    alignment: TextAlignment,
    verticalAlignment: TextVerticalAlignment,
    baselineShift: string,
  ) {
    mat3Mult(this.transform, svgTransform, combinedTransform);
    this.drawText(x, y, combinedTransform, fontSize, text, style, alignment, verticalAlignment, baselineShift);
  }

  protected drawText(
    _x: number,
    _y: number,
    _transform: Mat3,
    _fontsize: SheetPixels,
    _text: string,
    _style: SVGPathStyle,
    _horizontalAlignment?: TextAlignment,
    _verticalAlignment?: TextVerticalAlignment,
    _baselineShift?: string,
  ) {
    // Expected to be overridden by subclasses.
  }

  public appendArcPoints(
    _transformation: Mat3,
    _x: number,
    _y: number,
    _xRadius: number,
    _yRadius: number,
    _startAngleRad: number,
    _endAngleRad: number,
    _clockwise: boolean = true,
  ) {
    // Expected to be overridden by subclasses.
  }

  public quadraticCurveTo(aCPx: number, aCPy: number, aX: number, aY: number) {
    const p0 = this.getCurrentPoint();
    if (!p0) {
      return;
    }
    const [p0x, p0y] = p0;
    for (const [, x, y] of quadraticCurveTo(p0x, p0y, aCPx, aCPy, aX, aY)) {
      this.lastCurve.push([x, y]);
    }
  }

  public bezierCurveTo(aCP1x: number, aCP1y: number, aCP2x: number, aCP2y: number, aX: number, aY: number) {
    const p0 = this.getCurrentPoint();
    if (!p0) {
      return;
    }
    const [p0x, p0y] = p0;
    for (const [, x, y] of cubicCurveTo(p0x, p0y, aCP1x, aCP1y, aCP2x, aCP2y, aX, aY)) {
      this.lastCurve.push([x, y]);
    }
  }

  private getCurrentPoint(): Vec2 | null {
    if (this.lastCurve.length > 0) {
      return this.lastCurve[this.lastCurve.length - 1];
    }
    return null;
  }
}

// This ShapePath also eagerly creates shapes as soon as possible. Only maintains the current path.
export class DirectShapePath<D extends DirectPath> {
  constructor(public currentPath: D) {}

  moveTo(x: number, y: number) {
    this.currentPath.resetWithPoint(x, y);
  }

  lineTo(x: number, y: number) {
    this.currentPath.appendPoint(x, y);
  }

  quadraticCurveTo(aCPx: number, aCPy: number, aX: number, aY: number) {
    this.currentPath.quadraticCurveTo(aCPx, aCPy, aX, aY);
  }

  bezierCurveTo(aCP1x: number, aCP1y: number, aCP2x: number, aCP2y: number, aX: number, aY: number) {
    this.currentPath.bezierCurveTo(aCP1x, aCP1y, aCP2x, aCP2y, aX, aY);
  }

  text(
    x: number,
    y: number,
    transformation: Mat3,
    fontSize: SheetPixels,
    text: string,
    style: SVGPathStyle,
    alignment: TextAlignment,
    verticalAlignment: TextVerticalAlignment,
    baselineShift: string,
  ) {
    this.currentPath.text(x, y, transformation, fontSize, text, style, alignment, verticalAlignment, baselineShift);
  }

  done(style: SVGPathStyle, svgTransform: Mat3) {
    this.currentPath.renderCurve(svgTransform, style);
  }
}

export class SVGDirectCallbacks<Path extends DirectPath> implements SVGParserCallbacks<DirectShapePath<Path>, Path> {
  private currentStyle: SVGPathStyle = {};
  constructor(private readonly currentShapePath: DirectShapePath<Path>) {}

  newShapePath(): DirectShapePath<Path> {
    this.currentShapePath.currentPath.reset();
    return this.currentShapePath;
  }

  newPath(): Path {
    return this.currentShapePath.currentPath;
  }

  setViewBox(x: number, y: number, width: number, height: number) {
    this.currentShapePath.currentPath.setViewBox(x, y, width, height);
  }

  setSvgWidth(width: number) {
    this.currentShapePath.currentPath.setSvgWidth(width);
  }

  setSvgHeight(height: number) {
    this.currentShapePath.currentPath.setSvgHeight(height);
  }

  setStyle(style: SVGPathStyle) {
    this.currentStyle = style;
  }

  // ShapePath related operations

  shapePathMoveTo(path: DirectShapePath<Path>, svgTransform: Mat3, x: number, y: number) {
    path.currentPath.renderCurve(svgTransform, this.currentStyle);
    path.moveTo(x, y);
  }

  shapePathLineTo(path: DirectShapePath<Path>, _svgTransform: Mat3, x: number, y: number) {
    path.lineTo(x, y);
  }

  shapePathQuadraticCurveTo(
    path: DirectShapePath<Path>,
    _svgTransform: Mat3,
    aCPx: number,
    aCPy: number,
    ax: number,
    ay: number,
  ) {
    path.quadraticCurveTo(aCPx, aCPy, ax, ay);
  }

  shapePathBezierCurveTo(
    path: DirectShapePath<Path>,
    _svgTransform: Mat3,
    aCP1x: number,
    aCP1y: number,
    aCP2x: number,
    aCP2y: number,
    ax: number,
    ay: number,
  ) {
    path.bezierCurveTo(aCP1x, aCP1y, aCP2x, aCP2y, ax, ay);
  }

  // `textAnchor` can be one of 'start', 'middle', 'end'
  // If null is returned from the callback the ShapePath is not added to the paths
  shapePathText(
    path: DirectShapePath<Path>,
    svgTransform: Mat3,
    text: string,
    style: SVGPathStyle,
    x: number,
    y: number,
    fontSize: SheetPixels,
    textAnchor: "start" | "middle" | "end",
    verticalAlignment: TextVerticalAlignment,
    baselineShift: string,
  ): DirectShapePath<Path> | null {
    const alignmentMap = {
      start: TextAlignment.LEFT,
      middle: TextAlignment.CENTER,
      end: TextAlignment.RIGHT,
    } as const;

    path.text(x, y, svgTransform, fontSize, text, style, alignmentMap[textAnchor], verticalAlignment, baselineShift);

    return path;
  }

  shapePathGetCurrentPath(path: DirectShapePath<Path>): Path {
    return path.currentPath;
  }

  shapePathAddSubpath(path: DirectShapePath<Path>, subPath: Path) {
    path.currentPath = subPath;
  }

  shapePathDone(path: DirectShapePath<Path>, _1: HTMLElement, style: SVGPathStyle, svgTransform: Mat3) {
    path.done(style, svgTransform);
  }

  // Path related operations

  pathAutoClose(path: Path, close: boolean) {
    path.autoClose = close;
  }

  pathCloseCurve(path: Path, point: Vec2, svgTransform: Mat3) {
    path.renderCurve(svgTransform, this.currentStyle);
    path.resetWithPoint(point[0], point[1]);
  }

  pathAbsEllipse(
    path: Path,
    svgTransform: Mat3,
    centerx: number,
    centery: number,
    xRadius: number,
    yRadius: number,
    aStartAngleRad: number,
    aEndAngleRad: number,
    aClockwise: boolean = false,
    aRotationRad: number = 0,
  ) {
    path.appendArcPoints(
      svgTransform,
      centerx,
      centery,
      xRadius,
      yRadius,
      aRotationRad + aStartAngleRad,
      aRotationRad + aEndAngleRad,
      aClockwise,
    );
  }

  pathAbsArc(
    path: Path,
    svgTransform: Mat3,
    centerx: number,
    centery: number,
    aRadius: number,
    aStartAngleRad: number,
    aEndAngleRad: number,
    aClockwise: boolean = false,
  ) {
    path.appendArcPoints(svgTransform, centerx, centery, aRadius, aRadius, aStartAngleRad, aEndAngleRad, aClockwise);
  }
}
